import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Sentry } from '@/util/sentry';

type GlobalBannerErrorBoundaryProps = {
  children: ReactNode;
  /** Which banner surface this guards, for the Sentry tag. */
  surface: 'global-banner-slot' | 'announcement-header-banner';
};

type GlobalBannerErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Keeps a failing banner from taking the app down with it.
 *
 * The banners this guards are mounted app-wide — the web slot renders from the
 * secure layout on every authenticated screen, and the native banner renders
 * into each screen's header. Without a boundary, one throw anywhere in the
 * banner tree blanks the whole authenticated app rather than the strip that
 * failed.
 *
 * The fallback renders nothing, deliberately. A banner is supplementary: an
 * error placeholder pinned above the nav on every screen would be a worse
 * outcome than the message being absent, and there is no recovery action a
 * reader could take. Silence here is the graceful degradation.
 *
 * This catches render-time failures. The data behind a banner is fetched in
 * hooks that run in the host component, above this boundary, so a throw during
 * eligibility still escapes — those paths swallow their own errors and report to
 * Sentry instead.
 */
export class GlobalBannerErrorBoundary extends Component<
  GlobalBannerErrorBoundaryProps,
  GlobalBannerErrorBoundaryState
> {
  constructor(props: GlobalBannerErrorBoundaryProps) {
    super(props);

    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): GlobalBannerErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        component: 'GlobalBannerErrorBoundary',
        boundaryType: 'banner',
        source: this.props.surface,
      },
      extra: {
        errorMessage: error?.message,
        errorName: error?.name,
        componentStack: errorInfo?.componentStack,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}
